import type { DailyCall, DailyLoadOptions } from '@daily-co/daily-js';
import axios from 'axios';
import type { SessionCallJoinErrorKind } from '@/components/session-call/sessionCallJoinUtils';

export const SESSION_CALL_VIDEO_ROOM_RETRY_MS = 5_000;

export function buildDailyJoinProperties(
	url: string,
	token: string | null,
	userName?: string,
	options?: Pick<DailyLoadOptions, 'startAudioOff' | 'startVideoOff'>,
): DailyLoadOptions {
	return {
		url,
		...(token ? { token } : {}),
		...(userName ? { userName } : {}),
		...(options?.startAudioOff !== undefined
			? { startAudioOff: options.startAudioOff }
			: {}),
		...(options?.startVideoOff !== undefined
			? { startVideoOff: options.startVideoOff }
			: {}),
	};
}

export function requiresKnockToJoin(
	viewerRole: 'user' | 'professional',
	token: string | null,
): boolean {
	return viewerRole === 'user' || token === null;
}

export function resolveVideoRoomApiError(
	error: unknown,
): { kind: SessionCallJoinErrorKind; message: string } | null {
	if (!axios.isAxiosError(error)) {
		return null;
	}

	const status = error.response?.status;
	const responseMessage =
		typeof error.response?.data === 'object' &&
		error.response.data !== null &&
		'message' in error.response.data &&
		typeof (error.response.data as { message?: unknown }).message ===
			'string'
			? (error.response.data as { message: string }).message
			: null;

	if (status === 403) {
		return {
			kind: 'not-allowed',
			message:
				responseMessage ??
				'Você não tem permissão para entrar nesta reunião.',
		};
	}

	if (status === 404 && responseMessage?.includes('No active video room')) {
		return {
			kind: 'room-not-ready',
			message:
				'A sala de vídeo ainda está sendo preparada. Aguarde alguns instantes.',
		};
	}

	if (status === 404) {
		return {
			kind: 'generic',
			message: responseMessage ?? 'Sessão não encontrada.',
		};
	}

	return null;
}

export async function joinSessionCallWithKnock(
	call: DailyCall,
	credentials: SessionCall.Credentials,
	displayName: string | undefined,
	options?: Pick<DailyLoadOptions, 'startAudioOff' | 'startVideoOff'>,
): Promise<'granted' | 'denied'> {
	await call.join(
		buildDailyJoinProperties(credentials.url, null, displayName, options),
	);

	const accessName = displayName?.trim() || 'Participante';
	const { granted } = await call.requestAccess({
		access: { level: 'full' },
		name: accessName,
	});

	return granted ? 'granted' : 'denied';
}
